import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { getEventFull } from '@/lib/api/core';
import { isMockMode } from '@/lib/mock-data';

/**
 * useEventFull — composición §6 del evento con los beneficios del partner de
 * First Class (`GET /v1/events/:id/full`).
 *
 * Devuelve `partner`, `flashSales`, `offers`, `merchandising`, `restaurant` y
 * el flag `grantsFcPoints`, ya normalizados a tipos de UI. Es de PRESENTACIÓN:
 * la sección de beneficios se hidrata aparte del flujo de compra (que vive en
 * el SeatPicker / checkout). Si falla o no hay partner, degrada de forma digna.
 *
 * Nota precios: api-core devuelve montos en pesos (decimal). El `formatCOP` de
 * commerce espera CENTAVOS, así que normalizamos *100 acá.
 */

export type PartnerSummary = {
  id: string;
  name: string;
  type: string | null;
  description: string | null;
  logoUrl: string | null;
  coverImage: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  discountPercentage: number | null;
  benefits: string | null;
};

export type PartnerFlashSale = {
  id: string;
  title: string;
  description: string | null;
  originalPriceCents: number | null;
  finalPriceCents: number | null;
  discountPercentage: number | null;
  image: string | null;
};

export type PartnerOffer = {
  id: string;
  title: string | null;
  description: string | null;
  discountPct: number | null;
  finalPriceCents: number | null;
  image: string | null;
};

export type PartnerProduct = {
  id: string;
  name: string;
  description: string | null;
  pricePublicCents: number | null;
  priceFirstClassCents: number | null;
  image: string | null;
};

export type EventPartnerComposition = {
  partner: PartnerSummary | null;
  flashSales: PartnerFlashSale[];
  offers: PartnerOffer[];
  merchandising: PartnerProduct[];
  restaurant: PartnerSummary | null;
  grantsFcPoints: boolean;
  hasPartnerBenefits: boolean;
};

type Dict = Record<string, unknown>;

function num(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Pesos (decimal del server) → centavos (lo que espera `formatCOP`). */
function toCents(value: unknown): number | null {
  const n = num(value);
  return n === null ? null : Math.round(n * 100);
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function mapPartner(raw: unknown): PartnerSummary | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Dict;
  return {
    id: String(r.id ?? ''),
    name: str(r.name) ?? 'Partner',
    type: str(r.type),
    description: str(r.description),
    logoUrl: str(r.logo_url),
    coverImage: str(r.cover_image),
    address: str(r.address),
    phone: str(r.phone),
    email: str(r.email),
    discountPercentage: num(r.discount_percentage),
    benefits: str(r.benefits),
  };
}

function asArray(value: unknown): Dict[] {
  return Array.isArray(value) ? (value as Dict[]) : [];
}

export function normalizeEventFull(raw: Dict | null | undefined): EventPartnerComposition {
  const partner = mapPartner(raw?.partner);
  const flashSales: PartnerFlashSale[] = asArray(raw?.flash_sales).map((r) => ({
    id: String(r.id ?? ''),
    title: str(r.title) ?? 'Flash sale',
    description: str(r.description),
    originalPriceCents: toCents(r.original_price),
    finalPriceCents: toCents(r.final_price),
    discountPercentage: num(r.discount_percentage),
    image: str(r.main_image),
  }));
  const offers: PartnerOffer[] = asArray(raw?.offers).map((r) => ({
    id: String(r.id ?? ''),
    title: str(r.title),
    description: str(r.description),
    discountPct: num(r.discount_pct),
    finalPriceCents: toCents(r.final_price),
    image: str(r.main_image),
  }));
  const merchandising: PartnerProduct[] = asArray(raw?.merchandising).map((r) => ({
    id: String(r.id ?? ''),
    name: str(r.name) ?? 'Producto',
    description: str(r.description),
    pricePublicCents: toCents(r.price_public),
    priceFirstClassCents: toCents(r.price_firstclass),
    image: str(r.image_url),
  }));
  const restaurant = mapPartner(raw?.restaurant);
  const grantsFcPoints = Boolean(raw?.grants_fc_points);
  const hasPartnerBenefits = Boolean(
    partner || flashSales.length > 0 || offers.length > 0 || merchandising.length > 0 || restaurant,
  );
  return {
    partner,
    flashSales,
    offers,
    merchandising,
    restaurant,
    grantsFcPoints,
    hasPartnerBenefits,
  };
}

const EMPTY_COMPOSITION: EventPartnerComposition = {
  partner: null,
  flashSales: [],
  offers: [],
  merchandising: [],
  restaurant: null,
  grantsFcPoints: false,
  hasPartnerBenefits: false,
};

export function useEventFull(eventId: string | undefined): UseQueryResult<EventPartnerComposition> {
  return useQuery({
    queryKey: ['event-full', eventId, isMockMode() ? 'mock' : 'live'],
    queryFn: async () => {
      if (!eventId) throw new Error('useEventFull: eventId is required');
      // En mock-mode no hay endpoint /full: devolvemos composición vacía para
      // que la sección simplemente no aparezca (sin romper la página).
      if (isMockMode()) return EMPTY_COMPOSITION;
      const raw = await getEventFull(eventId);
      return normalizeEventFull(raw as Dict | null);
    },
    enabled: !!eventId,
    staleTime: 60_000,
    retry: (failureCount, error) => {
      const status = (error as { status?: number } | null)?.status;
      if (status && status >= 400 && status < 500) return false;
      return failureCount < 2;
    },
  });
}
